import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { Brain, RefreshCw, Save, Lock, Unlock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const KEY_FIELDS = [
  "objectif", "ville_cible", "budget_max", "apport_disponible",
  "strategie_preferee", "situation_pro", "fiscalite_actuelle", "horizon", "contraintes",
];
const NUMERIC = ["budget_max", "apport_disponible", "revenus_mensuels", "capacite_epargne", "nb_biens"];
const LISTS = ["biens_detenus", "villes_etudiees"];

const toText = (v) => (Array.isArray(v) ? v.join(", ") : v === null || v === undefined ? "" : String(v));

export const InvestorProfileCard = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const hydrate = (res) => {
    setData((prev) => ({ ...(prev || {}), ...res.data }));
    const flat = {};
    Object.entries(res.data.fields || {}).forEach(([k, v]) => {
      flat[k] = toText(v);
    });
    setValues(flat);
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/investor-profile`, auth);
      hydrate(res);
    } catch {
      /* profil optionnel */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/investor-profile/refresh`, {}, { ...auth, timeout: 120000 });
      hydrate(res);
      toast.success(res.data.skipped ? "Profil deja a jour (relu il y a moins de 5 min)" : "Profil mis a jour depuis tes discussions");
    } catch {
      toast.error("Impossible de relire tes discussions pour le moment");
    } finally {
      setBusy(false);
    }
  };

  // On n'envoie QUE les champs reellement modifies : sinon tout le profil serait
  // verrouille et l'IA ne pourrait plus le completer automatiquement.
  const handleSave = async () => {
    const stored = data?.fields || {};
    const payload = {};
    Object.entries(values).forEach(([k, v]) => {
      const text = (v || "").trim();
      if (text === toText(stored[k]).trim()) return;
      if (NUMERIC.includes(k)) {
        payload[k] = text ? Number(text.replace(/[^\d.,-]/g, "").replace(",", ".")) : null;
      } else if (LISTS.includes(k)) {
        payload[k] = text ? text.split(",").map((s) => s.trim()).filter(Boolean) : [];
      } else {
        payload[k] = text || null;
      }
    });
    if (Object.keys(payload).length === 0) {
      toast.info("Aucune modification a enregistrer");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.put(`${API}/api/investor-profile`, { fields: payload }, auth);
      hydrate(res);
      toast.success("Profil enregistre - tes experts en tiennent compte");
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const handleUnlock = async (field) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/api/investor-profile`, { fields: {}, unlock: [field] }, auth);
      hydrate(res);
      toast.success("L'IA pourra remettre ce champ a jour");
    } catch {
      toast.error("Erreur");
    } finally {
      setBusy(false);
    }
  };

  const labels = data?.labels || {};
  const order = data?.field_order || KEY_FIELDS;
  const locked = data?.locked_fields || [];
  const filled = Object.keys(data?.fields || {});
  const visible = order.filter((f) => KEY_FIELDS.includes(f) || filled.includes(f));

  return (
    <Card data-testid="investor-profile-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="w-5 h-5" />
          Mon profil investisseur
        </CardTitle>
        <CardDescription>
          Tes experts s'en servent pour te repondre sans que tu repetes ta situation.
          Il se complete tout seul au fil de tes discussions - corrige ce que tu veux.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {filled.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="investor-profile-empty">
            Rien de memorise pour l'instant. Discute avec un expert, ou relis tes discussions ci-dessous.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          {visible.map((field) => (
            <div key={field} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  {labels[field] || field}
                  {locked.includes(field) && <Lock className="w-3 h-3" />}
                </label>
                {locked.includes(field) && (
                  <button
                    type="button"
                    onClick={() => handleUnlock(field)}
                    disabled={busy}
                    data-testid={`unlock-field-${field}`}
                    className="text-[0.65rem] uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
                  >
                    <Unlock className="w-3 h-3" />
                    Laisser l'IA gerer
                  </button>
                )}
              </div>
              <Input
                data-testid={`profile-field-${field}`}
                value={values[field] || ""}
                placeholder="Non renseigne"
                onChange={(e) => setValues({ ...values, [field]: e.target.value })}
              />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Button onClick={handleSave} disabled={busy} data-testid="save-investor-profile-btn">
            <Save className="w-4 h-4 mr-2" />
            Enregistrer
          </Button>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={busy}
            data-testid="refresh-investor-profile-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${busy ? "animate-spin" : ""}`} />
            Relire mes discussions
          </Button>
        </div>
        {data?.updated_at && (
          <p className="text-xs text-muted-foreground" data-testid="investor-profile-updated">
            Derniere mise a jour : {new Date(data.updated_at).toLocaleString("fr-FR")}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
